import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  type ComponentType,
} from 'react';
import { InlineLoading } from '@carbon/react';
import { debounce } from 'lodash-es';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { useModalFacets } from './template-hooks';
import { DateRangeTemplate } from './templates/date-range-template';
import { NumericRangeTemplate } from './templates/numeric-range-template';
import { ValueHistogramTemplate } from './templates/value-histogram-template';
import { MultiSelectTemplate } from './templates/multi-select-template';
import { MultiSelectListTemplate } from './templates/multi-select-list-template';
import { StateCountyTreeTemplate } from './templates/state-county-tree-template';
import { RadioTemplate } from './templates/radio-template';
import { PicklistTemplate } from './templates/picklist-template';
import { TextTemplate } from './templates/text-template';
import { SurveyAreaTemplate } from './templates/survey-area-template';
import { MultiSelectTextSearchComboTemplate } from './templates/multi-select-text-search-combo-template';
import { CheckboxTemplate } from './templates/checkbox-template';
import { FilterFieldScope, FilterVariantContext } from './context';
import type {
  FilterFieldConfig,
  FilterTemplateProps,
  FilterVariant,
} from '../types';
import style from './filter-modal.module.scss';

interface FilterFormProps {
  activeCategory: string;
  /** How the fields are laid out; omitted, the filter modal's own layout. */
  variant?: FilterVariant;
}

type FilterType = FilterFieldConfig['type'];

// Each filter type's template, in the order the modal groups them.
const TEMPLATES: [FilterType, ComponentType<FilterTemplateProps>][] = [
  ['date-range', DateRangeTemplate],
  ['picklist', PicklistTemplate],
  ['radio', RadioTemplate],
  ['text', TextTemplate],
  ['survey-area', SurveyAreaTemplate],
  ['numeric-range', NumericRangeTemplate],
  ['value-histogram', ValueHistogramTemplate],
  ['multi-select', MultiSelectTemplate],
  ['multi-select-list', MultiSelectListTemplate],
  ['tree-table', StateCountyTreeTemplate],
  ['multi-select-text-search-combo', MultiSelectTextSearchComboTemplate],
  ['checkbox', CheckboxTemplate],
];
const TEMPLATE_FOR = new Map(TEMPLATES);

const FACET_DEBOUNCE_MS = 300;

// Filter types whose options come from the facet aggregation — the ones that
// look empty until the counts load (the value histogram draws from its facet
// too). Date/numeric/text/checkbox don't.
const FACET_DRIVEN_TYPES = new Set([
  'multi-select',
  'multi-select-list',
  'picklist',
  'radio',
  'tree-table',
  'multi-select-text-search-combo',
  'value-histogram',
]);

/**
 * The filter fields of one category, each through its type's template, staging
 * edits into the dataset's pending filters. Remounted on a category change or
 * when the modal opens, so templates holding their own input state (multi-
 * selects, typeaheads, the tree) re-read the staged filters and any dropdown
 * search starts over.
 * @param {FilterFormProps} props The form props.
 * @return {JSX.Element} The form.
 */
export function FilterForm({
  activeCategory,
  variant = 'modal',
}: FilterFormProps) {
  const atoms = useDatasetAtoms();
  const isOpen = useAtomValue(atoms.filterModalOpenAtom);
  return (
    <FilterFormFields
      key={`${activeCategory}:${isOpen}`}
      activeCategory={activeCategory}
      variant={variant}
    />
  );
}

/**
 * The form's fields, as FilterForm describes.
 * @param {Required<FilterFormProps>} props The form props.
 * @return {JSX.Element} The fields.
 */
function FilterFormFields({
  activeCategory,
  variant,
}: Required<FilterFormProps>) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const [pendingFilters, setPendingFilters] = useAtom(atoms.pendingFiltersAtom);
  const refreshFacets = useSetAtom(atoms.refreshFacetsAtom);
  const facets = useModalFacets();
  const facetsLoading = useAtomValue(atoms.modalFacetsLoadingAtom);
  const rail = variant === 'rail';

  const categoryFields = useMemo(
    () => config.filters.filter((f) => f.category === activeCategory),
    [config.filters, activeCategory]
  );

  // Show a loader while the first facet counts are still loading — this category
  // has facet-driven fields, a refresh is in flight, and nothing's loaded yet.
  const showFacetLoader =
    facetsLoading &&
    !facets &&
    categoryFields.some((f) => FACET_DRIVEN_TYPES.has(f.type));

  const types = useMemo(
    () => new Set(categoryFields.map((f) => f.type)),
    [categoryFields]
  );

  // Transient dropdown search state (facet_search_* keys). These drive live
  // facet counts only — they never stage into pending/search filters.
  const [facetSearch, setFacetSearch] = useState<Record<string, unknown>>({});

  // Form values read straight from pendingFilters (the single source of truth)
  // so external edits — chip removal in the preview, Clear All — stay in sync.
  // The transient facet_search_* keys are overlaid on top.
  const formValues = useMemo(
    () => ({ ...pendingFilters, ...facetSearch }),
    [pendingFilters, facetSearch]
  );

  // Mirror of pendingFilters that cascaded writes fired in one tick (tree:
  // state + its counties) accumulate onto for the facet payload below —
  // otherwise each reads the same stale value and the debounce keeps only the
  // last, dropping the earlier write from the counts.
  const pendingRef = useRef(pendingFilters);
  useLayoutEffect(() => {
    pendingRef.current = pendingFilters;
  }, [pendingFilters]);

  // ─── Debounced Facet Refresh ───

  const debouncedRefreshFacets = useMemo(
    () =>
      debounce((values: Record<string, unknown>) => {
        refreshFacets(values);
      }, FACET_DEBOUNCE_MS),
    [refreshFacets]
  );

  useEffect(() => {
    return () => debouncedRefreshFacets.cancel();
  }, [debouncedRefreshFacets]);

  const handleFormChange = useCallback(
    (key: string, value: unknown) => {
      const isFacetSearch = key.startsWith('facet_search_');

      // facet_search_* is transient dropdown state and must never leak into
      // pending/search payloads. Everything else auto-stages straight into
      // pendingFilters — the preview and the footer "Apply Filters" read it.
      const nextFacetSearch = isFacetSearch
        ? { ...facetSearch, [key]: value }
        : facetSearch;
      if (isFacetSearch) {
        setFacetSearch(nextFacetSearch);
      } else {
        // Accumulate onto the ref so a second cascaded write in the same tick
        // sees the first; the atom write stays functional for safe merging.
        pendingRef.current = { ...pendingRef.current, [key]: value };
        setPendingFilters((prev) => ({ ...prev, [key]: value }));
      }

      // Build the facet payload from the accumulated pending ref (the setState
      // above hasn't applied yet). refreshFacets drops applied text-search
      // params so they don't narrow the counts.
      // A typeahead search always needs its matching options fetched.
      if (!rail || isFacetSearch) {
        debouncedRefreshFacets({ ...pendingRef.current, ...nextFacetSearch });
      }
    },
    [facetSearch, rail, debouncedRefreshFacets, setPendingFilters]
  );

  if (types.size === 0) {
    return <div>Select a filter category</div>;
  }

  return (
    <div className={style.formWrapper}>
      <div className={rail ? style.formContentCompact : style.formContent}>
        {!rail && <h5 className={style.formTitle}>{activeCategory}</h5>}
        {showFacetLoader ? (
          <InlineLoading description="Loading options…" />
        ) : (
          <FilterVariantContext.Provider value={variant}>
            {rail
              ? categoryFields.map((field) => {
                  const Template = TEMPLATE_FOR.get(field.type);
                  if (!Template) return null;
                  return (
                    <FilterFieldScope.Provider key={field.id} value={field.id}>
                      <Template
                        category={activeCategory}
                        formValues={formValues}
                        onFormChange={handleFormChange}
                      />
                    </FilterFieldScope.Provider>
                  );
                })
              : TEMPLATES.filter(([type]) => types.has(type)).map(
                  ([type, Template]) => (
                    <Template
                      key={type}
                      category={activeCategory}
                      formValues={formValues}
                      onFormChange={handleFormChange}
                    />
                  )
                )}
          </FilterVariantContext.Provider>
        )}
      </div>
    </div>
  );
}
